import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import * as Icon from "@heroicons/vue/24/solid";
import router from "@/router/index.js";
import { useAuthStore } from "@/stores/AuthStore";
import {
  canAccessNavigationArea,
  CUSTOMER_RECORD_ROLES,
  CUSTOMER_SERVICE_ROLES,
  FLEET_ROLES,
} from "@/utils/navigationAccess";

const RECENT_NAVIGATION_STORAGE_KEY = "neptune_recent_navigation";
const MAX_RECENT_ITEMS = 6;

const readRecentItems = () => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(RECENT_NAVIGATION_STORAGE_KEY) || "[]",
    );

    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const writeRecentItems = (items) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      RECENT_NAVIGATION_STORAGE_KEY,
      JSON.stringify(items),
    );
  } catch {
    // Ignore storage failures.
  }
};

const recentItems = ref(readRecentItems());

const AREA_DEFINITIONS = [
  {
    key: "operations",
    label: "Operations",
    description: "Calendar, jobs and daily delivery work",
    icon: Icon.CalendarDaysIcon,
    defaultLink: "/calendar",
  },
  {
    key: "customers",
    label: "Customers",
    description: "Customers, quotes and support",
    icon: Icon.UsersIcon,
    defaultLink: "/tickets",
  },
  {
    key: "people",
    label: "People",
    description: "Staff, vacancies and policies",
    icon: Icon.UserGroupIcon,
  },
  {
    key: "finance",
    label: "Finance",
    description: "Invoices, payroll and reporting",
    icon: Icon.CurrencyPoundIcon,
  },
  {
    key: "fleet",
    label: "Fleet",
    description: "Vehicles and fuel cards",
    icon: Icon.TruckIcon,
  },
  {
    key: "admin",
    label: "Admin",
    description: "System and super-user tools",
    icon: Icon.Cog6ToothIcon,
  },
];

const CREATE_ACTIONS = [
  { label: "Job", to: "/reference/create", icon: Icon.BriefcaseIcon },
  { label: "Quote", to: "/quotes/create", icon: Icon.DocumentTextIcon },
  { label: "Ticket", to: "/tickets/create", icon: Icon.ChatBubbleLeftEllipsisIcon },
  { label: "Customer", to: "/customer/create", icon: Icon.UserPlusIcon },
  { label: "Invoice", to: "/invoice/create", icon: Icon.DocumentDuplicateIcon },
  { label: "Requisition", to: "/requisitions", icon: Icon.ClipboardDocumentListIcon },
  { label: "Staff", to: "/staff/create", icon: Icon.UserCircleIcon },
  { label: "Supplier", to: "/suppliers/create", icon: Icon.BuildingStorefrontIcon },
  { label: "Vehicle", to: "/vehicles/create", icon: Icon.TruckIcon },
  { label: "Policy", to: "/policies/new", icon: Icon.DocumentCheckIcon },
];

const getRouteKey = (routeRecord) =>
  routeRecord.name ? `name:${routeRecord.name}` : `path:${routeRecord.path}`;

export const canAccessRouteMeta = (meta = {}, authStore) => {
  if (!authStore?.isLoggedIn && meta?.requiresAuth !== false) {
    return false;
  }

  const hasFinanceRole = authStore.hasFinanceRole();
  const hasPayrollFinance = authStore.hasAnyRole(["finance-department", "super"]);
  const hasCustomerAccess = authStore.hasAnyRole(CUSTOMER_RECORD_ROLES);
  const hasCustomerServiceAccess = authStore.hasAnyRole(CUSTOMER_SERVICE_ROLES);
  const hasFleetAccess = authStore.hasAnyRole(FLEET_ROLES);

  if (meta?.requiresCustomerService && !hasCustomerServiceAccess) {
    return false;
  }
  if (meta?.requiresCustomerRecords && !hasCustomerAccess) {
    return false;
  }
  if (meta?.requiresPayrollFinance && !hasPayrollFinance) {
    return false;
  }
  if (meta?.requiresFinance && !hasFinanceRole) {
    return false;
  }
  if (
    meta?.requiresSupplierRead &&
    !hasFinanceRole &&
    !authStore.hasRole("operational") &&
    !authStore.hasRole("super")
  ) {
    return false;
  }
  if (meta?.requiresFinanceAudits && !hasFinanceRole) {
    return false;
  }
  if (meta?.requiresSuper && !authStore.hasRole("super")) {
    return false;
  }
  if (meta?.requiresManager && !authStore.hasRole("manager")) {
    return false;
  }
  if (
    meta?.requiresManagerOrSuper
    && !authStore.hasAnyRole(["manager", "super"])
  ) {
    return false;
  }
  if (meta?.requiresFleet && !hasFleetAccess) {
    return false;
  }

  return true;
};

const normalizeRouteRecords = () => {
  const seen = new Set();

  return router
    .getRoutes()
    .filter((routeRecord) => {
      if (!routeRecord?.meta?.navSection) {
        return false;
      }

      const routeKey = getRouteKey(routeRecord);
      if (seen.has(routeKey)) {
        return false;
      }

      seen.add(routeKey);
      return true;
    })
    .map((routeRecord) => ({
      name: routeRecord.name,
      path: routeRecord.path,
      meta: routeRecord.meta || {},
    }));
};

const routeRecords = normalizeRouteRecords();

const findAreaDefinition = (key) =>
  AREA_DEFINITIONS.find((area) => area.key === key) || null;

export const recordRecentNavigation = ({
  route,
  label = "",
}) => {
  if (!route?.meta?.navSection || route?.meta?.navRecent === false) {
    return;
  }

  if (String(route.fullPath || "").startsWith("/login")) {
    return;
  }

  const area = findAreaDefinition(route.meta.navSection);
  const resolvedLabel =
    String(label || "").trim() ||
    route.meta.navLabel ||
    route.meta.title ||
    area?.label ||
    "Page";

  const nextItem = {
    label: resolvedLabel,
    link: route.fullPath,
    areaKey: route.meta.navSection,
    areaLabel: area?.label || "",
    recordedAt: Date.now(),
  };

  const deduped = [
    nextItem,
    ...recentItems.value.filter((item) => item?.link !== nextItem.link),
  ].slice(0, MAX_RECENT_ITEMS);

  recentItems.value = deduped;
  writeRecentItems(deduped);
};

export function useNavigationModel() {
  const authStore = useAuthStore();
  const route = useRoute();

  const accessibleRouteRecords = computed(() =>
    routeRecords.filter((routeRecord) =>
      canAccessRouteMeta(routeRecord.meta, authStore),
    ),
  );

  const areaItemsMap = computed(() => {
    const nextMap = new Map();

    accessibleRouteRecords.value.forEach((routeRecord) => {
      if (!routeRecord.meta?.navVisible) {
        return;
      }

      const sectionKey = routeRecord.meta.navSection;
      if (!nextMap.has(sectionKey)) {
        nextMap.set(sectionKey, []);
      }

      nextMap.get(sectionKey).push({
        name: routeRecord.meta.navLabel || routeRecord.meta.title || routeRecord.name,
        link: routeRecord.path,
        order: Number(routeRecord.meta.navOrder ?? 999),
      });
    });

    nextMap.forEach((items, key) => {
      items.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
      nextMap.set(key, items);
    });

    return nextMap;
  });

  const visibleAreas = computed(() =>
    AREA_DEFINITIONS.filter((area) =>
      canAccessNavigationArea(area.key, authStore),
    ).map((area) => {
      const items = areaItemsMap.value.get(area.key) || [];
      const preferredItem = items.find((item) => item.link === area.defaultLink);

      return {
        ...area,
        items,
        defaultLink: preferredItem?.link || items[0]?.link || null,
      };
    }).filter((area) => area.items.length > 0),
  );

  const currentAreaKey = computed(() => route.meta?.navSection || null);
  const currentArea = computed(
    () => visibleAreas.value.find((area) => area.key === currentAreaKey.value) || null,
  );
  const currentAreaItems = computed(() => currentArea.value?.items || []);

  const visibleCreateItems = computed(() =>
    CREATE_ACTIONS.filter((item) => {
      const resolved = router.resolve(item.to);
      return canAccessRouteMeta(resolved.meta, authStore);
    }),
  );

  const visibleRecentItems = computed(() =>
    recentItems.value.filter((item) => {
      if (!item?.link || !item?.areaKey) {
        return false;
      }

      const resolved = router.resolve(item.link);
      return canAccessRouteMeta(resolved.meta, authStore);
    }),
  );

  return {
    visibleAreas,
    currentArea,
    currentAreaKey,
    currentAreaItems,
    visibleCreateItems,
    recentItems: visibleRecentItems,
  };
}
